import React, { useState, useEffect } from 'react'
import axios from 'axios'

interface Alert {
  id: number
  type: string
  severity: 'low' | 'medium' | 'high' | 'critical'
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed'
  title: string
  description: string
  accountId?: string
  service?: string
  detectedAt: string
  resolvedAt?: string
  acknowledgedAt?: string
  acknowledgedBy?: string
  acknowledgedByEmail?: string
  metadata?: any
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({
    status: 'all',
    severity: 'all',
    type: 'all'
  })

  useEffect(() => {
    loadAlerts()
  }, [filter])

  const loadAlerts = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const params = new URLSearchParams()
      if (filter.status !== 'all') params.append('status', filter.status)
      if (filter.severity !== 'all') params.append('severity', filter.severity)
      if (filter.type !== 'all') params.append('type', filter.type)

      const response = await axios.get(`/api/alerts?${params.toString()}`, { headers })
      setAlerts(response.data.alerts || [])
    } catch (error) {
      console.error('Failed to load alerts:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateAlertStatus = async (alertId: number, status: string) => {
    try {
      const token = localStorage.getItem('finops_token')
      const headers = { Authorization: `Bearer ${token}` }

      const response = await axios.patch(`/api/alerts/${alertId}`, { status }, { headers })

      // Update local state with the response data
      setAlerts((prev) =>
        prev.map((alert) =>
          alert.id === alertId
            ? {
                ...alert,
                status: status as any,
                resolvedAt: status === 'resolved' ? new Date().toISOString() : alert.resolvedAt,
                acknowledgedAt: response.data.alert?.acknowledgedAt || alert.acknowledgedAt,
                acknowledgedBy: response.data.alert?.acknowledgedBy || alert.acknowledgedBy,
                acknowledgedByEmail:
                  response.data.alert?.acknowledgedByEmail || alert.acknowledgedByEmail
              }
            : alert
        )
      )
    } catch (error) {
      console.error('Failed to update alert:', error)
    }
  }

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'critical':
        return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-l-red-500' }
      case 'high':
        return { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-l-amber-500' }
      case 'medium':
        return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-l-yellow-500' }
      default:
        return { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-l-blue-500' }
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'open':
        return { bg: 'bg-red-100', text: 'text-red-800' }
      case 'acknowledged':
        return { bg: 'bg-amber-100', text: 'text-amber-800' }
      case 'resolved':
        return { bg: 'bg-green-100', text: 'text-green-800' }
      case 'dismissed':
        return { bg: 'bg-gray-100', text: 'text-gray-800' }
      default:
        return { bg: 'bg-gray-100', text: 'text-gray-800' }
    }
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
        <p className="ml-4 text-gray-600">Loading alerts...</p>
      </div>
    )
  }

  const openAlerts = alerts.filter((a) => a.status === 'open').length
  const criticalAlerts = alerts.filter((a) => a.severity === 'critical').length
  const thisMonthAlerts = alerts.filter(
    (a) => new Date(a.detectedAt).getMonth() === new Date().getMonth()
  ).length

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
        <p className="mt-1 text-sm text-gray-600">Monitor cost anomalies and budget alerts</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-blue-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Total Alerts</p>
              <p className="text-2xl font-bold text-gray-900">{alerts.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${openAlerts > 0 ? 'bg-red-100' : 'bg-green-100'}`}
            >
              <svg
                className={`w-6 h-6 ${openAlerts > 0 ? 'text-red-600' : 'text-green-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Open Alerts</p>
              <p
                className={`text-2xl font-bold ${openAlerts > 0 ? 'text-red-600' : 'text-green-600'}`}
              >
                {openAlerts}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div
              className={`w-12 h-12 rounded-xl flex items-center justify-center ${criticalAlerts > 0 ? 'bg-red-100' : 'bg-gray-100'}`}
            >
              <svg
                className={`w-6 h-6 ${criticalAlerts > 0 ? 'text-red-600' : 'text-gray-600'}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Critical</p>
              <p
                className={`text-2xl font-bold ${criticalAlerts > 0 ? 'text-red-600' : 'text-gray-600'}`}
              >
                {criticalAlerts}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-gray-900">{thisMonthAlerts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-5 h-5 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <span className="font-medium text-gray-700">Filters</span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 lg:flex-1">
            <select
              value={filter.status}
              onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value }))}
              className="form-input"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="acknowledged">Acknowledged</option>
              <option value="resolved">Resolved</option>
              <option value="dismissed">Dismissed</option>
            </select>

            <select
              value={filter.severity}
              onChange={(e) => setFilter((prev) => ({ ...prev, severity: e.target.value }))}
              className="form-input"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={filter.type}
              onChange={(e) => setFilter((prev) => ({ ...prev, type: e.target.value }))}
              className="form-input"
            >
              <option value="all">All Types</option>
              <option value="anomaly">Cost Anomaly</option>
              <option value="budget">Budget Alert</option>
              <option value="threshold">Threshold Alert</option>
            </select>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {alerts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No alerts found</h3>
            <p className="text-sm text-gray-500">
              No alerts match your current filters. Try adjusting your search criteria.
            </p>
          </div>
        ) : (
          alerts.map((alert) => {
            const severityConfig = getSeverityConfig(alert.severity)
            const statusConfig = getStatusConfig(alert.status)

            return (
              <div
                key={alert.id}
                className={`bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden border-l-4 ${severityConfig.border} hover:shadow-md transition-shadow`}
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Alert Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <h3 className="text-base font-semibold text-gray-900">{alert.title}</h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${severityConfig.bg} ${severityConfig.text}`}
                        >
                          {alert.severity}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full ${statusConfig.bg} ${statusConfig.text}`}
                        >
                          {alert.status}
                        </span>
                      </div>

                      <p className="text-sm text-gray-600 mb-4">{alert.description}</p>

                      <div className="flex items-center flex-wrap gap-4 text-sm text-gray-500">
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">Type:</span>
                          <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {alert.type}
                          </span>
                        </span>
                        {alert.service && (
                          <span className="inline-flex items-center gap-1">
                            <span className="font-medium">Service:</span>
                            <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                              {alert.service}
                            </span>
                          </span>
                        )}
                        {alert.accountId && (
                          <span className="inline-flex items-center gap-1">
                            <span className="font-medium">Account:</span>
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                              {alert.accountId}
                            </span>
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          {formatDate(alert.detectedAt)}
                        </span>
                      </div>

                      {/* Acknowledged Info */}
                      {alert.status === 'acknowledged' && alert.acknowledgedBy && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <span className="inline-flex items-center gap-2 text-sm text-amber-700 bg-amber-50 px-3 py-1.5 rounded-lg">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                            Acknowledged by{' '}
                            <span className="font-medium">{alert.acknowledgedBy}</span>
                            {alert.acknowledgedAt && (
                              <span className="text-amber-600">
                                on {formatDate(alert.acknowledgedAt)}
                              </span>
                            )}
                          </span>
                        </div>
                      )}

                      {/* Resolved Info */}
                      {alert.status === 'resolved' && alert.resolvedAt && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <span className="inline-flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                              />
                            </svg>
                            Resolved on {formatDate(alert.resolvedAt)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    {alert.status === 'open' && (
                      <div className="flex flex-wrap gap-2 lg:flex-col lg:flex-shrink-0">
                        <button
                          onClick={() => updateAlertStatus(alert.id, 'acknowledged')}
                          className="px-4 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
                        >
                          Acknowledge
                        </button>
                        <button
                          onClick={() => updateAlertStatus(alert.id, 'resolved')}
                          className="px-4 py-2 text-sm font-medium text-green-700 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Resolve
                        </button>
                        <button
                          onClick={() => updateAlertStatus(alert.id, 'dismissed')}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          Dismiss
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
